import {useState} from 'react';
import {MaskedInput, MaskedInputProps} from '@/components/app/inputs';
import {Input, InputProps} from '@/components/ui/input';
import {Notice} from '@/components/app/feedback';
import {cn} from '@/lib/utils';
import {Form, type FormProps} from '../../../app/components/Form';
import {useActiveTenant} from '../../core/hooks/useActiveTenant';
export interface BiotwinNameFormFieldValues {
  name: string;
}
export interface BiotwinNameFormProps extends Omit<FormProps<BiotwinNameFormFieldValues>, 'fields'> {}
export const BiotwinNameForm = (props: BiotwinNameFormProps) => {
  return (
    <Form<BiotwinNameFormFieldValues>
      submitButton={{
        children: 'Save',
        variant: 'warning',
      }}
      render={({renderError, renderFieldByName, renderSubmitButton}) => {
        return (
          <>
            <BiotwinNameDescription />
            {renderError()}
            {renderFieldByName('name')}
            {renderSubmitButton()}
          </>
        );
      }}
      {...props}
      fields={[
        {
          label: 'Name',
          name: 'name',
          rules: {
            maxLength: 50,
            required: true,
          },
          type: BiotwinNameInputText,
          typeOptions: {
            autoFocus: true,
            maxLength: 50,
          },
        },
      ]}
    />
  );
};
function BiotwinNameDescription() {
  const tenant = useActiveTenant();
  const pattern = tenant?.biotypeNamePattern;
  if (pattern == null) {
    return null;
  }
  const parsedPattern = parseBiotwinNamePattern(pattern);
  const patternInfo = generateBiotwinNamePatternInfo(parsedPattern);
  return <Notice tone={'info'} text={patternInfo} />;
}
type BiotwinNameInputTextProps = Omit<InputProps, 'value' | 'onChange'> & {
  value?: string | null;
  onValueChange: (value: string) => void;
};
export function BiotwinNameInputText({className, onValueChange, value, ...otherProps}: BiotwinNameInputTextProps) {
  const tenant = useActiveTenant();
  const mask = tenant?.biotypeNameMask;
  if (mask)
    return (
      <MaskedInput {...otherProps} className={className} mask={mask} onValueChange={onValueChange} value={value} />
    );
  const pattern = parseBiotwinNamePattern(tenant?.biotypeNamePattern ?? null);
  const allowed = (char: string) =>
    /[\p{L}]/u.test(char)
      ? pattern.allowLetters
      : /[\p{N}]/u.test(char)
        ? pattern.allowDigits
        : /\s/u.test(char)
          ? pattern.allowSpaces
          : pattern.allowSpecialChars && (!pattern.specialChars.length || pattern.specialChars.includes(char));
  return (
    <Input
      {...otherProps}
      className={cn(className, pattern.leadingChar && 'text-right')}
      maxLength={pattern.maxLength + (pattern.leadingChar ? 1 : 0)}
      minLength={pattern.minLength}
      value={value ?? ''}
      onFocus={(event) => {
        if (pattern.leadingChar) event.target.setSelectionRange(event.target.value.length, event.target.value.length);
      }}
      onChange={(event) => {
        const text = Array.from(event.target.value).filter(allowed).join('').slice(0, pattern.maxLength);
        onValueChange(pattern.leadingChar && text ? text.padStart(pattern.maxLength, pattern.leadingChar) : text);
      }}
    />
  );
}
const tokenDigits = '[\\p{N}]';
const tokenLetters = '[\\p{L}]';
const tokenSpaces = '[\\p{Z}]';
const tokenSpecialCharacters = '[^\\p{N}\\p{L}\\p{Z}]';
const patternRegExp = /\^(?:\[(.)]\{0})?\((.*)\)\{(\d+),(\d+)}\$/;
export const parseBiotwinNamePattern = (biotypeNamePattern: string | null): ParsedBiotwinNamePattern => {
  const match = biotypeNamePattern?.match(patternRegExp);
  if (match == null) {
    return {
      allowDigits: true,
      allowLetters: true,
      allowSpaces: true,
      allowSpecialChars: true,
      leadingChar: '',
      maxLength: 50,
      minLength: 0,
      specialChars: [],
    };
  }
  const [_, leadingChar = '', tokensString = '', minLength, maxLength] = match;
  const tokens = tokensString.split('|');
  const specialChars = tokens
    .filter((token) => token !== tokenDigits)
    .filter((token) => token !== tokenLetters)
    .filter((token) => token !== tokenSpaces)
    .filter((token) => token !== tokenSpecialCharacters)
    .join('|')
    .slice(1, -1)
    .split('');
  return {
    allowDigits: tokens.includes(tokenDigits),
    allowLetters: tokens.includes(tokenLetters),
    allowSpaces: tokens.includes(tokenSpaces),
    allowSpecialChars: tokens.includes(tokenSpecialCharacters) || specialChars.length > 0,
    leadingChar,
    maxLength: Number(maxLength),
    minLength: Number(minLength),
    specialChars,
  };
};
interface ParsedBiotwinNamePattern {
  allowDigits: boolean;
  allowLetters: boolean;
  allowSpaces: boolean;
  allowSpecialChars: boolean;
  leadingChar: string;
  maxLength: number;
  minLength: number;
  specialChars: string[];
}
function generateBiotwinNamePatternInfo(parsedBiotwinNamePattern: ParsedBiotwinNamePattern) {
  const {allowDigits, allowLetters, allowSpaces, allowSpecialChars, maxLength, minLength, specialChars} =
    parsedBiotwinNamePattern;
  const charTypes = [
    {
      label: 'letters',
      value: allowLetters,
    },
    {
      label: 'digits',
      value: allowDigits,
    },
    {
      label: 'spaces',
      value: allowSpaces,
    },
    {
      label: specialChars.join(', ') || 'special characters',
      value: allowSpecialChars,
    },
  ]
    .filter(({value}) => value)
    .map(({label}) => label);
  const charTypesString = charTypes.join(', ');
  const lengthString = minLength === 0 ? `at most ${maxLength}` : `between ${minLength} and ${maxLength}`;
  return `Biotwin name ${charTypes.length === 1 ? 'can only' : 'must'} contain ${charTypesString} and be ${lengthString} characters long.`;
}
